import ApiServer from "../../ApiServer.ts";
import * as Html from "../../util/Html.ts";
import {PlaceCache} from "../../server/objects/MapBeans.ts";
//import * as googleApi from "../../googleApi.ts";
import TPLpicker from "../../widget/place-picker.mustache";

type IPlace = InstanceType<typeof ApiServer.types.IPlace>;
type GeocodedAddress = InstanceType<typeof ApiServer.types.GeocodedAddress> & {
  latitude: number;
  longitude: number;
};

type SearchResult = {
  search: string;
  places: IPlace[];
  items: any[];
};

interface Coordinates {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

export {initDom,destroyDom}
Html.registerWidget('widget/form/place-picker',{initDom,destroyDom})

const FACTOR_TO_RADIAN: number = Math.PI / 180;

function worm(add1: Coordinates, add2: Coordinates): number {
  const lat1 = add1.latitude * FACTOR_TO_RADIAN;
  const lat2 = add2.latitude * FACTOR_TO_RADIAN;
  const lng1 = add1.longitude * FACTOR_TO_RADIAN;
  const lng2 = add2.longitude * FACTOR_TO_RADIAN;

  return 6365000 * Math.sqrt((lat2 - lat1) * (lat2 - lat1) + Math.pow((lng2 - lng1) * Math.cos(lat2), 2));
}




function filterDuplicatePlace(places: IPlace[]): IPlace[] {
  const mapByAddress: Record<string, IPlace> = {}; //will contain all place by address (keeping the smaller radius)
  const result = places.filter((place) => { //we fill mapbyAdress while filtering the not temporary
    const oldPlace = mapByAddress[place.address.formattedAddress];
    if(!oldPlace || place.address.radius < oldPlace.address.radius){
      mapByAddress[place.address.formattedAddress] = place;
    }
    return !place.isTemporary;
  });

  Object.values(mapByAddress).forEach((place) => {
    if(place.isTemporary){
      result.push(place);
    }
  });
  return result;
}

// WeakMap to store widget state per input element
interface WidgetState {
  proposeDiv: HTMLElement;
  lastSearch: SearchResult;
  closed: boolean;
  intervalId: number;
  handlers: {
    setPosition: () => void;
    close: () => void;
    itemClick: (evt: Event) => void;
    inputClick: (evt: Event) => void;
    inputFocus: () => void;
  };
}

const widgetState = new WeakMap<HTMLInputElement, WidgetState>();

function initWidget(obj: HTMLInputElement, places: IPlace[]): void {
  places = filterDuplicatePlace(places);
  const lastSearch: SearchResult = {search:'', places: places, items: []};
  let closed = true;

  const proposeDiv = document.createElement('div');
  proposeDiv.className = 'fw-place-picker';
  obj.parentElement!.insertBefore(proposeDiv, obj.nextSibling);

  const setPosition = (): void => {
    const rect = obj.getBoundingClientRect();
    const parentRect = obj.offsetParent ? (obj.offsetParent as HTMLElement).getBoundingClientRect() : {left: 0, top: 0};

    proposeDiv.style.left = (rect.left - parentRect.left) + 'px';
    proposeDiv.style.width = obj.offsetWidth + 'px';
    proposeDiv.style.top = (rect.top - parentRect.top + obj.offsetHeight - 1) + 'px';
    proposeDiv.style.maxHeight = '300px';
  };

  obj.setAttribute('autocomplete','off');

//    var geocoder = new window.google.maps.Geocoder();

  if (obj.dataset.useGeolocation && navigator.geolocation) {
    navigator.geolocation.getCurrentPosition((geoposition: GeolocationPosition) => {

      places.sort((a, b) => {
        const deltaA = worm(a.address, geoposition.coords);
        const deltaB = worm(b.address, geoposition.coords);
        return deltaA > deltaB ? 1 : -1;
      });
      //we have a radius intersection with the closest place
      const coords = geoposition.coords;
      if(places.length > 0 && worm(places[0].address, coords) < (coords.accuracy || 0) + places[0].address.radius){
        obj.value = places[0].address.formattedAddress;
        setPlaceId(places[0].metaId);
      }else{
        //else we georeverse to get an address
        ApiServer.georeverse(Math.round(coords.latitude * 1000000), Math.round(coords.longitude * 1000000), (georeversed) => {
          lastSearch.search = georeversed.formattedAddress;
          obj.value = georeversed.formattedAddress;
        });
      }
    });
  }


  const propose = (): void => {
    setPosition();

    lastSearch.places.sort((a, b) => {
      if(a.isTemporary !== b.isTemporary){
        return b.isTemporary ? -1 : 1;
      }
      return 0;
    });
    Html.setHtml(proposeDiv, TPLpicker, lastSearch);
    proposeDiv.style.display = 'block';
  };

  const close = (): void => {
    proposeDiv.style.display = 'none';
  };

  const setPlaceId = (placeId: string | null): void => {
    const form = obj.closest('form');
    if(form){
      const placeIdInput = form.querySelector<HTMLInputElement>('[name="placeId"]');
      if(placeIdInput){
        placeIdInput.value = placeId || '';
      }
    }
  };

  const itemClick = (evt: Event): void => {
    const target = (evt.target as HTMLElement).closest('.fw-place-picker-item');
    if(target){
      closed = true;
      obj.value = target.getAttribute('data-fw-formattedaddress') || '';
      setPlaceId(target.getAttribute('data-fw-meta-id'));
      obj.dispatchEvent(new Event('change'));
    }
  };

  const inputClick = (evt: Event): void => {
    evt.stopPropagation();
    propose();
  };

  const inputFocus = (): void => {
    closed = false;
    obj.value = lastSearch.search;
    obj.select();
    propose();
  };

  window.addEventListener('resize', setPosition);
  document.addEventListener('click', close);
  proposeDiv.addEventListener('click', itemClick);
  obj.addEventListener('click', inputClick);
  obj.addEventListener('focus', inputFocus);

  const intervalId = window.setInterval(() => {
    if(closed) return;
    const value = obj.value;
    const lowerCaseValue = value.trim().toLowerCase();

    if(value===lastSearch.search){
      return;
    }
    if (lowerCaseValue.length===0) {
      lastSearch.search = value;
      lastSearch.places = places;
      lastSearch.items = [];
      propose();
      return;
    }
    //seek in place
    lastSearch.search = value;
    lastSearch.places = places.filter((o) => {
      if(o.name.toLowerCase().indexOf(lowerCaseValue) !== -1){
        return true;
      }
      if(o.address.formattedAddress.toLowerCase().indexOf(lowerCaseValue) !== -1){
        return true;
      }
      return false;
    });
    lastSearch.items = [];

    propose();
  },200);

  // Store state for cleanup
  widgetState.set(obj, {
    proposeDiv,
    lastSearch,
    closed,
    intervalId,
    handlers: {
      setPosition,
      close,
      itemClick,
      inputClick,
      inputFocus
    }
  });
}

function initDom(obj: HTMLElement): void {
  const input = obj as HTMLInputElement;
  PlaceCache.need().then((places: IPlace[]) => {
    initWidget(input, places);
  });
}

function destroyDom(obj: HTMLElement): void {
  const input = obj as HTMLInputElement;
  const state = widgetState.get(input);

  if(!state) return;

  clearInterval(state.intervalId);
  window.removeEventListener('resize', state.handlers.setPosition);
  document.removeEventListener('click', state.handlers.close);
  // Note: pas besoin de removeEventListener sur proposeDiv - il sera supprimé
  input.removeEventListener('click', state.handlers.inputClick);
  input.removeEventListener('focus', state.handlers.inputFocus);

  // Remove the dropdown element (nettoie automatiquement ses listeners)
  state.proposeDiv.remove();

  // Clean up state
  widgetState.delete(input);
}